/**
 * Helper for persisting list-page state (search, paging, sort, filters)
 * across navigation. Backed by sessionStorage so it survives in-tab
 * navigation but doesn't leak across tabs/restarts.
 *
 * Usage in a list component:
 *   private readonly stateKey = "client-list";
 *
 *   ngOnInit() {
 *     Object.assign(this, loadListState(this.stateKey, {
 *       searchText: "",
 *       pageIndex: 1,
 *       pageSize: 10,
 *       sortField: "createdAt",
 *       sortOrder: -1,
 *     }));
 *     this.fetch();
 *   }
 *
 *   private rememberState() {
 *     saveListState(this.stateKey, {
 *       searchText: this.searchText,
 *       pageIndex: this.pageIndex,
 *       pageSize: this.pageSize,
 *       sortField: this.sortField,
 *       sortOrder: this.sortOrder,
 *     });
 *   }
 */

const PREFIX = "rh.listState.";

function isPageReload(): boolean {
  try {
    // Modern browsers: PerformanceNavigationTiming
    const nav = performance.getEntriesByType?.("navigation")?.[0] as any;
    if (nav?.type) return nav.type === "reload";
    // Legacy fallback
    const legacy = (performance as any).navigation;
    return legacy?.type === 1; // TYPE_RELOAD
  } catch {
    return false;
  }
}

export function loadListState<T extends object>(
  key: string,
  defaults: T,
): T {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      // UX rule: on a full browser refresh, always reset pagination to page 1.
      // (Filters/sort can remain sticky.)
      if (isPageReload() && "pageIndex" in parsed) {
        delete (parsed as any).pageIndex;
      }
      return { ...defaults, ...parsed } as T;
    }
  } catch {
    /* corrupt entry; fall through to defaults */
  }
  return { ...defaults };
}

export function saveListState<T extends object>(key: string, state: T): void {
  try {
    sessionStorage.setItem(PREFIX + key, JSON.stringify(state));
  } catch {
    /* sessionStorage may be full / disabled - just skip persistence */
  }
}

export function clearListState(key: string): void {
  try {
    sessionStorage.removeItem(PREFIX + key);
  } catch {
    /* ignore */
  }
}
